import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { rememberLeague } from "../lib/league";
import { timeZoneOptions, detectTimeZone, currentTimeIn } from "../shared/timezones";

// Starting a league, available to anyone at any time.
//
// This used to appear only for someone who belonged to no league at all, which
// meant a member who loved the game had no way to start one for a different
// group. That caps every person at one league and removes the only way this
// spreads: somebody plays, likes it, and starts one for their own people.
export function NewLeague({ firstTime = false }: { firstTime?: boolean }) {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [tz, setTz] = useState(detectTimeZone());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function create() {
    setBusy(true);
    setErr("");
    try {
      const { data: u } = await supabase.auth.getUser();
      if (u.user && displayName) {
        await supabase
          .from("users")
          .upsert({ id: u.user.id, email: u.user.email ?? "", display_name: displayName }, { onConflict: "id" });
      }
      const r = await api<{ league: { id: string } }>(`/api/league/admin`, {
        body: { op: "create", name, timezone: tz, displayName },
      });
      if (!r?.league?.id) throw new Error("The server did not return a league. Check the server settings in Cloudflare.");
      rememberLeague(r.league.id);
      navigate(`/league/${r.league.id}/commissioner`);
    } catch (e: any) {
      setErr(e?.message ?? "Something went wrong creating the league.");
      setBusy(false);
    }
  }

  return (
    <div className="app">
      <div className="header">
        <h1>Start a league</h1>
        {!firstTime && <button className="link" onClick={() => navigate("/")}>Cancel</button>}
      </div>
      <p className="muted">
        You&apos;ll be the commissioner, and a player like everybody else. You can invite your people on the
        next screen.
      </p>

      {/* Landing here after being invited almost always means signing in with an
          address the invite never went to. Creating a second league instead is a
          confusing mess to undo, so say so before they do it. */}
      {firstTime && (
        <div className="card">
          <span className="tag">Were you invited to a league?</span>
          <p>
            Then you&apos;re in the wrong place. This starts a brand new league. You&apos;re seeing it because{" "}
            <b>{user?.email || "this email"}</b> wasn&apos;t the address your invite went to.
          </p>
          <button className="btn ghost" onClick={async () => { await signOut(); window.location.href = "/"; }}>
            Sign out and use a different email
          </button>
        </div>
      )}

      <label className="muted">League name</label>
      <input className="input" placeholder="The Cousins League" value={name} onChange={(e) => setName(e.target.value)} />

      <label className="muted">Your name, as your people will see it</label>
      <input className="input" placeholder="Your name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />

      <label className="muted">Timezone <span style={{ opacity: 0.7 }}>(drop times follow this)</span></label>
      <select className="input" value={tz} onChange={(e) => setTz(e.target.value)}>
        {timeZoneOptions(tz).map((z) => <option key={z.id} value={z.id}>{z.label}</option>)}
      </select>
      {currentTimeIn(tz) && <p className="muted">Right now it is {currentTimeIn(tz)} there.</p>}

      {err && <p className="error">{err}</p>}
      <button className="btn primary" disabled={busy || !name.trim() || !displayName.trim()} onClick={create}>
        {busy ? "Creating…" : "Create league"}
      </button>
    </div>
  );
}
